import { Model } from '@/model/Model'
import { BetCooldownState } from '@/states/reels_spinning/BetCooldownState'
import { BaseParentState } from '@stateMachine/BaseParentState'
import { IStateMachine } from '@stateMachine/IStateMachine'
import { GameStatesEnum } from '../GameStatesEnum'
import { FreeSpinsActivateState } from '@/states/freespins/FreeSpinsActivateState'
import { FreeSpinsIntroState } from '@/states/freespins/FreeSpinsIntroState'
import { FreeSpinsSummaryState } from '@/states/freespins/FreeSpinsSummaryState'
import { FreeSpinsRetriggerState } from '@/states/freespins/FreeSpinsRetriggerState'
import { HoldAndWinJackpotPopupState } from '@/states/holdandwin/HoldAndWinJackpotPopupState'
import { HoldAndWinSummaryState } from '@/states/holdandwin/HoldAndWinSummaryState'
import { BigWinState } from '@/states/winings/BigWinState'
import { FsCoinCollectState } from '@/states/winings/FsCoinCollectState'
import { RegularWinningsState } from '@/states/winings/RegularWinningsState'
import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'
import { SharedPopupManager } from '@dragonframework/modules'
import { getHoldAndWinFinalWinAmount, isEndingHoldAndWinRound } from '@/states/holdandwin/HoldAndWinFinalWin'
import { WinningUtil } from '@/managers/winning/WinningUtil'
import { MaxExposureState } from '@/states/round/MaxExposureState'
import { isMaxWinCapReached } from '@/states/round/MaxWinCap'

export class FinalState extends BaseParentState {
    private _isWaitingHoldAndWinCollect: boolean = false

    public constructor(stateMachine: IStateMachine, model: Model, id: string) {
        super(stateMachine, model, id)

        // Runs first after the collect animation, before big-win/regular-win presentation
        // and the End Popup (placeholder for the Jackpot Popup).
        this.addChild(HoldAndWinJackpotPopupState, GameStatesEnum.HOLD_AND_WIN_JACKPOT_POPUP_STATE)

        /** final winnings states **/
        this.addChild(RegularWinningsState, GameStatesEnum.REGULAR_WINNINGS_STATE)
        this.addChild(FsCoinCollectState, 'FS_COIN_COLLECT_STATE')
        this.addChild(BigWinState, GameStatesEnum.BIG_WIN_STATE)
        this.addChild(BetCooldownState, GameStatesEnum.BET_COOLDOWN_STATE)

        /** free spins states **/
        this.addChild(FreeSpinsActivateState, GameStatesEnum.FREE_SPINS_ACTIVATE_STATE)
        this.addChild(FreeSpinsIntroState, GameStatesEnum.FREE_SPINS_INTRO_STATE)
        this.addChild(FreeSpinsRetriggerState, GameStatesEnum.FREE_SPINS_RETRIGGER_STATE)
        this.addChild(FreeSpinsSummaryState, GameStatesEnum.FREE_SPINS_SUMMARY_STATE)
        this.addChild(HoldAndWinSummaryState, GameStatesEnum.HOLD_AND_WIN_SUMMARY_STATE)

        //## Last: the capped-round notice runs after every feature end popup, before the round closes into STOP_ROUND_STATE.
        this.addChild(MaxExposureState, 'MAX_EXPOSURE_STATE')
    }

    public begin() {
        if (isEndingHoldAndWinRound(this._model)) {
            this._isWaitingHoldAndWinCollect = true
            ;(GameSignals as any).holdAndWinCollectComplete?.connect(this.onHoldAndWinCollectComplete)
            GameSignals.finalState.emit()

            // Rapid skip can finish collect before this state connects — proceed immediately.
            const collectAlreadyDone =
                !(Facade as any).holdAndWinCollectInProgress &&
                Boolean((Facade as any).holdAndWinPendingBaseTransition)
            if (collectAlreadyDone) {
                this.onHoldAndWinCollectComplete()
            }
            return
        }

        // Do not sync balance here during FS — Win Meter updates later (RegularWinnings /
        // H&W collect). UIManager credits balance together with the Win field.
        super.begin()
        GameSignals.finalState.emit()
    }

    private onHoldAndWinCollectComplete = () => {
        if (!this._isWaitingHoldAndWinCollect) {
            return
        }

        this._isWaitingHoldAndWinCollect = false
        ;(GameSignals as any).holdAndWinCollectComplete?.disconnect(this.onHoldAndWinCollectComplete)

        const finalWinAmount = getHoldAndWinFinalWinAmount(this._model)
        this._model.bigWinLevel = WinningUtil.getBigWinLevel(finalWinAmount, this._model.currentBaseBet)
        // Win Meter is credited when the H&W total-win popup opens (HoldAndWinSummaryState).
        super.begin()
    }

    public end = () => {
        // Se c'è una promo offer in attesa di conferma, andiamo in STOP_ROUND per mostrare la popup
        if (SharedPopupManager.isPromoOfferPendingConfirmation) {
            console.log('[FinalState] promo offer pending, going to STOP_ROUND instead of auto-spinning')
            this._stateMachine.setState(GameStatesEnum.STOP_ROUND_STATE)
            return
        }

        // Capped round: whatever the response still promises, nothing more may be played.
        if (isMaxWinCapReached(this._model)) {
            this.releaseHoldAndWinFlags()
            this._stateMachine.setState(GameStatesEnum.STOP_ROUND_STATE)
            return
        }

        const nextRoundType = String(this._model.spinData?.next_round_type || '').toLowerCase()
        const respinsLeft = Number(this._model.spinData?.respins_left ?? 0)
        const hasRespinsLeft =
            (nextRoundType === 'respins' || nextRoundType === 'respin') &&
            Number.isFinite(respinsLeft) &&
            respinsLeft > 0
        const wasHoldAndWinActive = Boolean(this._model.spinState.isHoldAndWinActive)

        if (hasRespinsLeft) {
            this._model.spinState.isHoldAndWinActive = true

            // Base-game -> respin flow: winlines (if any) -> dim + trigger coin wins -> transition.
            if (!wasHoldAndWinActive) {
                this._stateMachine.setState(GameStatesEnum.HOLD_AND_WIN_ACTIVATE_STATE)
                return
            }

            this._stateMachine.setState(GameStatesEnum.START_SPIN_STATE)
            return
        }

        this.releaseHoldAndWinFlags()

        if (this._model.spinData?.freebet && this._model.spinData.freebet.amount_left > 0) {
            // 0-win FS spins never touch the Win field — still push server balance before next spin.
            GameSignals.balanceHasChanged.emit(this._model.balance)
            this._stateMachine.setState(GameStatesEnum.START_SPIN_STATE)
            return
        }

        if (this._model.hasPromoSpinsRemaining()) {
            GameSignals.balanceHasChanged.emit(this._model.balance)
            this._stateMachine.setState(GameStatesEnum.START_SPIN_STATE)
            return
        }

        this._stateMachine.setState(GameStatesEnum.STOP_ROUND_STATE)
    }

    private releaseHoldAndWinFlags(): void {
        const isHoldAndWinPostSequenceActive = Boolean(
            (Facade as any).holdAndWinCollectInProgress || (Facade as any).holdAndWinPendingBaseTransition
        )

        if (!isHoldAndWinPostSequenceActive) {
            this._model.spinState.isHoldAndWinActive = false
            return
        }

        if (!this._model.spinState.isHoldAndWinActive) {
            // Recover from stale post-sequence flags that can survive edge flows and block FS summary routing.
            ;(Facade as any).holdAndWinCollectInProgress = false
            ;(Facade as any).holdAndWinPendingBaseTransition = false
        }
    }

    public cleanUp() {
        this._isWaitingHoldAndWinCollect = false
        ;(GameSignals as any).holdAndWinCollectComplete?.disconnect(this.onHoldAndWinCollectComplete)
    }
}
